/**
 * @fileoverview Zod schemas for chat event payloads exchanged with n8n and the app.
 * @description Runtime validation and static typing for chat-related events.
 */

import { z } from 'zod';

// Common fields
export const SessionId = z.string().min(1, 'sessionId required');
export const UserId = z.string().min(1, 'userId required');

// Meta information passed alongside context actions (goal/plan/task)
export const ChatMeta = z
  .object({
    entityType: z.enum(['goal', 'plan', 'task']).optional(),
    id: z.string().optional(),
  })
  .passthrough()
  .optional();

// Chat message event payload
export const ChatMessageEvent = z
  .object({
    eventType: z.literal('chat_message'),
    action: z.enum([
      'send',
      'onboarding',
      // Allow future actions without breaking validation by permitting passthrough
    ]),
    source: z.string().min(1),
    sessionId: SessionId,
    userId: UserId.optional(),
    correlationId: z.string().optional(),
    userAgent: z.string().optional(),
    url: z.string().url().optional(),
    timestamp: z.union([z.number(), z.string()]).optional(),
    intent: z.string().optional(),
    meta: ChatMeta,
  })
  .passthrough();

export type ChatMessageEvent = z.infer<typeof ChatMessageEvent>;

// Helpers
export function parseChatMessageEvent(input: unknown): ChatMessageEvent {
  return ChatMessageEvent.parse(input);
}

// Input for chatService.createMessage
export const ChatMessageInput = z
  .object({
    content: z.string().min(1, 'content required'),
    role: z.enum(['user', 'assistant', 'system']).or(z.string().min(1)),
    session_id: SessionId,
    user_id: UserId,
  })
  .passthrough();

export type ChatMessageInput = z.infer<typeof ChatMessageInput>;
